import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  DepartmentScheduleResponse,
  DepartmentScheduleQuery,
} from '@/types/schedule.types';

/**
 * Format date to YYYY-MM-DD using local timezone
 * Avoids toISOString() which converts to UTC and can shift date by -1 day in positive timezones
 */
const formatLocalDate = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get schedules for all employees in a department
 * Returns resolved schedules using priority logic:
 * work_schedules -> schedule_exceptions -> template_calendar -> template_pattern
 */
export const useDepartmentSchedule = (params: DepartmentScheduleQuery | null) => {
  return useQuery<DepartmentScheduleResponse>({
    queryKey: ['departmentSchedule', params],
    queryFn: async () => {
      if (!params) throw new Error('Department schedule params are required');

      const response = await api.get('/api/v1/schedules/by-department', {
        params: {
          departmentId: params.departmentId,
          from: params.from,
          to: params.to,
          includeInactive: params.includeInactive || false,
        },
      });

      // API returns data directly, not wrapped
      return response.data || response;
    },
    enabled: !!params?.departmentId && !!params?.from && !!params?.to,
    // Cache for 5 minutes, stale after 1 minute
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Get current month's schedule for a department
 */
export const useDepartmentMonthSchedule = (
  departmentId: string | null,
  year?: number,
  month?: number,
) => {
  // Calculate first and last day of month
  const now = new Date();
  const targetYear = year ?? now.getFullYear();
  const targetMonth = month ?? now.getMonth(); // 0-indexed

  const firstDay = new Date(targetYear, targetMonth, 1);
  const lastDay = new Date(targetYear, targetMonth + 1, 0);

  const from = formatLocalDate(firstDay);
  const to = formatLocalDate(lastDay);

  return useDepartmentSchedule(
    departmentId
      ? {
          departmentId,
          from,
          to,
        }
      : null,
  );
};

/**
 * Get week schedule for a department
 * @param weekOffset - 0 for current week, -1 for previous, +1 for next, etc.
 */
export const useDepartmentWeekSchedule = (
  departmentId: string | null,
  weekOffset: number = 0,
) => {
  const now = new Date();
  // Get Monday of current week
  const dayOfWeek = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + weekOffset * 7);

  // Get Sunday of that week
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const from = formatLocalDate(monday);
  const to = formatLocalDate(sunday);

  return useDepartmentSchedule(
    departmentId
      ? {
          departmentId,
          from,
          to,
        }
      : null,
  );
};

/**
 * Get schedule for specific date range
 */
export const useDepartmentRangeSchedule = (
  departmentId: string | null,
  from: string | null,
  to: string | null,
  includeInactive: boolean = false,
) => {
  return useDepartmentSchedule(
    departmentId && from && to
      ? {
          departmentId,
          from,
          to,
          includeInactive,
        }
      : null,
  );
};

// Helper types for components
export interface DayStats {
  date: string;
  totalWorking: number;
  totalOff: number;
  hasExceptions: boolean;
}

/**
 * Calculate daily statistics from department schedule
 */
export function calculateDayStats(
  schedule: DepartmentScheduleResponse | undefined,
): DayStats[] {
  if (!schedule) return [];

  const statsMap = new Map<string, DayStats>();

  for (const employee of schedule.employees) {
    for (const day of employee.schedule) {
      const existing = statsMap.get(day.date) || {
        date: day.date,
        totalWorking: 0,
        totalOff: 0,
        hasExceptions: false,
      };

      if (day.is_working_day) {
        existing.totalWorking++;
      } else {
        existing.totalOff++;
      }

      if (day.source === 'schedule_exception') {
        existing.hasExceptions = true;
      }

      statsMap.set(day.date, existing);
    }
  }

  return Array.from(statsMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date),
  );
}
